/**
 * Yönetim → Altyapı sekmesi.
 *
 * Amaç: sistemi sıfırdan kuran birinin koda ve SSH'a dokunmadan tüm servis
 * adreslerini görüp ayarlaması, ve her birinin gerçekten ayakta olduğunu
 * tek tıkla doğrulaması.
 *
 * 2026-07-25 denetimi bu sekmenin neden gerektiğini canlı olarak gösterdi:
 * `MACDM_EXTRACTOR_TOKEN` sunucu ortamında hiç tanımlı olmadığı için kod
 * varsayılanı `CHANGE_ME` gidiyor, her belge yüklemesi HTTP 401 alıyordu.
 * 2,5 ay boyunca kimse fark etmedi — çünkü `healthz` extractor'ı hiç
 * kontrol etmiyor ve değer hiçbir arayüzde görünmüyordu.
 *
 * TASARIM İLKESİ — dürüst düğme: her alan, değişikliğin ANINDA mı etkili
 * olduğunu yoksa YENİDEN BAŞLATMA mı gerektirdiğini söyler. Boot'ta
 * tüketilen alanlar (veritabanı bağlantısı, gömme modeli, havuz limitleri)
 * düzenlenebilir olarak SUNULMAZ; salt okunur gösterilir. Kaydedilip
 * hiçbir şeyi etkilemeyen bir alan, olmayan alandan kötüdür.
 */
import { useState, useEffect, useCallback } from "react";
import type {
  SystemConfig,
  InfrastructureSnapshot,
  InfraProbeResult,
} from "../../transport/authClient";
import {
  getInfrastructure,
  probeInfrastructure,
  updateSystemConfig,
} from "../../transport/authClient";

type ProbeMap = Record<string, InfraProbeResult>;

const PROBE_LABELS: Record<string, string> = {
  database: "Veritabanı (PostgreSQL)",
  qdrant: "Vektör Deposu (Qdrant)",
  extractor: "Belge Çıkarıcı",
  embed: "Gömme Servisi",
  rerank: "Yeniden Sıralama",
  research: "Web Araştırma",
  stealth_fetch: "Ekran Görüntüsü Servisi",
  searxng: "SearXNG",
};

export function InfrastructureTab() {
  const [snap, setSnap] = useState<InfrastructureSnapshot | null>(null);
  const [draft, setDraft] = useState<SystemConfig>({});
  const [dirtySecrets, setDirtySecrets] = useState<Set<string>>(new Set());
  const [probes, setProbes] = useState<ProbeMap>({});
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getInfrastructure();
      setSnap(s);
      setDraft({});
      setDirtySecrets(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Altyapı bilgisi alınamadı");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function current(key: string): string | number | boolean | null {
    if (key in draft) {
      const v = (draft as Record<string, unknown>)[key];
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        return v;
      }
      return null;
    }
    return snap?.editable[key]?.value ?? null;
  }

  function set(key: keyof SystemConfig, value: unknown) {
    setDraft((d) => ({ ...d, [key]: value }));
    if (snap?.editable[key as string]?.secret) {
      setDirtySecrets((s) => new Set(s).add(key as string));
    }
  }

  async function runProbe(target?: string) {
    setProbing(target ?? "all");
    try {
      const res = await probeInfrastructure(target ? [target] : []);
      setProbes((p) => ({ ...p, ...res.results }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Test başarısız");
    } finally {
      setProbing(null);
    }
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // Maskeli görüntü değerini ("••••1234") sunucuya GERİ GÖNDERMEK,
      // gerçek sırrı o maskeyle ezmek demektir. Sadece kullanıcının
      // gerçekten yazdığı sırlar gönderilir.
      const payload: SystemConfig = {};
      for (const [key, value] of Object.entries(draft)) {
        const isSecret = snap?.editable[key]?.secret ?? false;
        if (isSecret && !dirtySecrets.has(key)) continue;
        if (isSecret && typeof value === "string" && value.startsWith("••••")) continue;
        (payload as Record<string, unknown>)[key] = value;
      }
      if (Object.keys(payload).length === 0) {
        setError("Kaydedilecek değişiklik yok.");
        return;
      }
      await updateSystemConfig(payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      await load();
      // Kaydettikten hemen sonra test et: "kaydettim ama çalışıyor mu?"
      // sorusunu operatörün sormasına gerek kalmasın.
      await runProbe();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="text-center py-12 text-slate-500">Altyapı yükleniyor…</div>;
  }
  if (!snap) {
    return (
      <div className="text-center py-12 text-red-400">
        {error ?? "Altyapı bilgisi alınamadı"}
      </div>
    );
  }

  const dirty = Object.keys(draft).length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Altyapı</h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Sistemin bağlandığı tüm servisler. Buradaki değerler koda gömülü
            değildir — sıfırdan kurulumda tek ayar noktası burasıdır.
            Değişiklikler kaydedildiği anda etkili olur; yeniden başlatma
            gerektiren alanlar aşağıda salt okunur olarak listelenir.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => void runProbe()}
            disabled={probing !== null}
            className="px-4 py-2 text-sm rounded-lg border border-white/10 hover:bg-white/5 disabled:opacity-40 transition-colors"
          >
            {probing === "all" ? "Test ediliyor…" : "Tümünü Test Et"}
          </button>
          <button
            onClick={() => void save()}
            disabled={saving || !dirty}
            className="px-5 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 disabled:opacity-40 transition-all"
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300">
          {error}
        </div>
      )}
      {success && (
        <div className="px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-300">
          Kaydedildi ve anında etkili oldu.
        </div>
      )}

      {/* Sağlık tablosu */}
      <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-sm">Servis Sağlığı</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Canlı probe. Sistem sağlık uç noktası yalnızca beş bileşene
              bakar; burada sekizinin hepsi test edilir.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {Object.keys(PROBE_LABELS).map((key) => {
            const p = probes[key];
            return (
              <div
                key={key}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/5"
              >
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    !p
                      ? "bg-slate-600"
                      : p.ok
                        ? "bg-emerald-400"
                        : "bg-red-400"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-300">
                    {PROBE_LABELS[key]}
                  </p>
                  {p && (
                    <p
                      className={`text-[10px] mt-0.5 truncate ${
                        p.ok ? "text-slate-500" : "text-red-400"
                      }`}
                      title={p.error ?? p.detail ?? ""}
                    >
                      {p.ok
                        ? `${p.detail ?? "sağlıklı"} · ${p.latencyMs}ms`
                        : p.error}
                    </p>
                  )}
                  {!p && (
                    <p className="text-[10px] text-slate-600 mt-0.5">
                      test edilmedi
                    </p>
                  )}
                </div>
                <button
                  onClick={() => void runProbe(key)}
                  disabled={probing !== null}
                  className="text-[10px] px-2 py-1 rounded border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-40 shrink-0 transition-colors"
                >
                  {probing === key ? "…" : "Test"}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <InfraSection
        accent="cyan"
        badge="RAG"
        title="Vektör Deposu ve Belge İşleme"
        desc="Yüklenen belgelerin metne çevrildiği, parçalandığı ve arandığı hat."
      >
        <TextField
          label="Qdrant Adresi"
          value={String(current("qdrant_url") ?? "")}
          onChange={(v) => set("qdrant_url", v)}
          placeholder="http://127.0.0.1:6333"
          hint="Vektör veritabanı. Adresi değiştirirseniz koleksiyon yeni sunucuda otomatik yaratılır."
        />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Koleksiyon Adı"
            value={String(current("qdrant_collection") ?? "")}
            onChange={(v) => set("qdrant_collection", v)}
            placeholder="macdm_rag"
          />
          <NumField
            label="Zaman Aşımı (sn)"
            value={Number(current("qdrant_timeout_s") ?? 8)}
            onChange={(v) => set("qdrant_timeout_s", v)}
            min={1}
            max={120}
            step={0.5}
          />
        </div>
        <TextField
          label="Belge Çıkarıcı Adresi"
          value={String(current("extractor_url") ?? "")}
          onChange={(v) => set("extractor_url", v)}
          placeholder="http://127.0.0.1:8903/doc/extract"
          hint="PDF/DOCX dosyalarından metin çıkaran servis."
        />
        <div className="grid grid-cols-2 gap-4">
          <SecretField
            label="Çıkarıcı Erişim Anahtarı"
            value={String(current("extractor_token") ?? "")}
            onChange={(v) => set("extractor_token", v)}
            hint="Yanlışsa belge yükleme HTTP 401 ile başarısız olur."
          />
          <NumField
            label="Çıkarıcı Zaman Aşımı (sn)"
            value={Number(current("extractor_timeout_s") ?? 45)}
            onChange={(v) => set("extractor_timeout_s", v)}
            min={5}
            max={300}
            step={5}
          />
        </div>
      </InfraSection>

      <InfraSection
        accent="violet"
        badge="EMB"
        title="Gömme ve Yeniden Sıralama"
        desc="Metinlerin vektöre çevrilmesi ve arama sonuçlarının yeniden sıralanması."
      >
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Gömme Servisi Adresi"
            value={String(current("embed_base_url") ?? "")}
            onChange={(v) => set("embed_base_url", v)}
            placeholder="http://127.0.0.1:8003/v1"
          />
          <SecretField
            label="Gömme API Anahtarı"
            value={String(current("embed_api_key") ?? "")}
            onChange={(v) => set("embed_api_key", v)}
          />
        </div>
        <p className="text-[11px] text-amber-400/80 -mt-1">
          Gömme modeli ve boyutu yeniden başlatma gerektirir; aşağıdaki salt
          okunur listede görünürler.
        </p>
        <BoolField
          label="Yeniden Sıralama Etkin"
          desc="Arama sonuçlarını ikinci bir modelle yeniden puanla"
          value={Boolean(current("rerank_enabled"))}
          onChange={(v) => set("rerank_enabled", v)}
        />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Yeniden Sıralama Adresi"
            value={String(current("rerank_base_url") ?? "")}
            onChange={(v) => set("rerank_base_url", v)}
            placeholder="http://127.0.0.1:8004"
          />
          <NumField
            label="Asgari Puan"
            value={Number(current("rerank_min_score") ?? 0)}
            onChange={(v) => set("rerank_min_score", v)}
            min={0}
            max={1}
            step={0.05}
            hint="Bu puanın altındaki sonuçlar atılır."
          />
        </div>
      </InfraSection>

      <InfraSection
        accent="orange"
        badge="WEB"
        title="Web Araştırma"
        desc="Tartışma sırasında canlı kaynak toplayan servisler."
      >
        <TextField
          label="Araştırma Servisi Adresi"
          value={String(current("research_url") ?? "")}
          onChange={(v) => set("research_url", v)}
          placeholder="http://127.0.0.1:8899/research"
        />
        <TextField
          label="SearXNG Adresi"
          value={String(current("searxng_self_hosted_url") ?? "")}
          onChange={(v) => set("searxng_self_hosted_url", v)}
          placeholder="http://127.0.0.1:8080"
          hint="Boş bırakılırsa yalnızca genel arama havuzu kullanılır."
        />
        <BoolField
          label="DuckDuckGo Devre Dışı"
          desc="Kurumsal ağ bu uç noktayı engelliyorsa açın — boşa bekleme olmaz"
          value={Boolean(current("research_disable_ddg"))}
          onChange={(v) => set("research_disable_ddg", v)}
        />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Ekran Görüntüsü Servisi"
            value={String(current("stealth_fetch_base_url") ?? "")}
            onChange={(v) => set("stealth_fetch_base_url", v)}
            placeholder="http://127.0.0.1:8896"
          />
          <SecretField
            label="Ekran Görüntüsü Anahtarı"
            value={String(current("stealth_fetch_auth_token") ?? "")}
            onChange={(v) => set("stealth_fetch_auth_token", v)}
          />
        </div>
      </InfraSection>

      <InfraSection
        accent="indigo"
        badge="RAG"
        title="Belge Parçalama Ayarları"
        desc="Yüklenen belgelerin nasıl bölündüğü ve kaç parçanın konseye verildiği."
      >
        <div className="grid grid-cols-2 gap-4">
          <NumField
            label="Parça Boyutu (karakter)"
            value={Number(current("rag_chunk_chars") ?? 2400)}
            onChange={(v) => set("rag_chunk_chars", v)}
            min={200}
            max={8000}
            step={100}
          />
          <NumField
            label="Parça Örtüşmesi (karakter)"
            value={Number(current("rag_chunk_overlap") ?? 320)}
            onChange={(v) => set("rag_chunk_overlap", v)}
            min={0}
            max={2000}
            step={20}
            hint="Parça boyutundan küçük olmalı."
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <NumField
            label="Tur Başına Parça Sayısı"
            value={Number(current("rag_top_k") ?? 8)}
            onChange={(v) => set("rag_top_k", v)}
            min={1}
            max={24}
            step={1}
            hint="Her konuşmacıya verilen alıntı sayısı."
          />
          <NumField
            label="Maks. Yükleme Boyutu (MB)"
            value={
              Math.round(
                (Number(current("rag_max_upload_bytes") ?? 20971520) /
                  (1024 * 1024)) *
                  10,
              ) / 10
            }
            onChange={(v) => set("rag_max_upload_bytes", Math.round(v * 1024 * 1024))}
            min={1}
            max={200}
            step={1}
          />
        </div>
      </InfraSection>

      {/* Salt okunur — yeniden başlatma gerektirenler */}
      <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 text-xs font-bold">
            RO
          </div>
          <div>
            <h3 className="font-semibold text-sm">
              Yeniden Başlatma Gerektiren Ayarlar
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Bu değerler servis açılışında okunup bağlantı havuzlarına ve
              önbelleklere işlendiği için çalışırken değiştirilemez. Buradan
              düzenlenebilir gösterilmemeleri bilinçlidir — kaydedilip hiçbir
              şeyi etkilemeyen bir alan, yanıltıcıdır. Değiştirmek için
              sunucudaki ortam dosyasını güncelleyip servisi yeniden başlatın.
            </p>
          </div>
        </div>
        <div className="mt-4 divide-y divide-white/5">
          {snap.readonly.map((item) => (
            <div key={item.key} className="flex items-baseline gap-4 py-2.5">
              <span className="text-xs text-slate-400 w-64 shrink-0">
                {item.label}
              </span>
              <code className="text-xs text-slate-300 font-mono break-all">
                {item.value === null || item.value === ""
                  ? "—"
                  : String(item.value)}
              </code>
              <code className="text-[10px] text-slate-600 ml-auto shrink-0">
                MACDM_{item.key.toUpperCase()}
              </code>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ─── Yapı taşları ─────────────────────────────────────────────────────── */

const ACCENTS: Record<string, string> = {
  cyan: "bg-cyan-500/10 text-cyan-400",
  violet: "bg-violet-500/10 text-violet-400",
  orange: "bg-orange-500/10 text-orange-400",
  indigo: "bg-indigo-500/10 text-indigo-400",
};

function InfraSection({
  accent,
  badge,
  title,
  desc,
  children,
}: {
  accent: string;
  badge: string;
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${ACCENTS[accent] ?? ACCENTS.indigo}`}
        >
          {badge}
        </div>
        <div>
          <h3 className="font-semibold text-sm">{title}</h3>
          <p className="text-xs text-slate-500">{desc}</p>
        </div>
        <span className="ml-auto text-[10px] px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
          anında etkili
        </span>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

const INPUT_CLS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500";

function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...(placeholder ? { placeholder } : {})}
        className={INPUT_CLS}
      />
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function SecretField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  const [revealed, setRevealed] = useState(false);
  const masked = value.startsWith("••••");
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <div className="relative">
        <input
          type={revealed || masked ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            // Maskeli değeri düzenlemeye çalışmak, gerçek sırrı "••••1234"
            // ile ezmeye yol açar. Odaklanınca alanı temizliyoruz ki
            // kullanıcı yeni değeri baştan yazsın.
            if (masked) onChange("");
          }}
          placeholder="değiştirmek için yeni değeri yazın"
          className={INPUT_CLS}
        />
        {!masked && value && (
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 hover:text-slate-300"
          >
            {revealed ? "gizle" : "göster"}
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">
        {hint ? `${hint} ` : ""}
        {masked ? "Kayıtlı değer gizlendi; boş bırakırsanız korunur." : ""}
      </p>
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  min,
  max,
  step,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          if (!Number.isNaN(n)) onChange(n);
        }}
        className={INPUT_CLS}
      />
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function BoolField({
  label,
  desc,
  value,
  onChange,
}: {
  label: string;
  desc: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between px-1">
      <div>
        <p className="text-sm font-medium text-slate-300">{label}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
          value ? "bg-indigo-600" : "bg-slate-700"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            value ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}
